import React, { useEffect, useState } from 'react'
import { Button, Col, DatePicker, Form, Input, Radio, Row } from 'antd'
import UploadDocument from 'components/global/UploadDocument'
import {
  Address,
  CategoryType,
  OrganizationResp,
  Photo,
} from 'types/globalTypes'
import { Link, useHistory } from 'react-router-dom'
import SportsSelect from './InputComps/SportsSelect'
import Location from './InputComps/Location'
import { LeagueStep1Payload } from 'types/league/league'
import moment from 'moment'
import {
  useCreateLeague,
  useUpdateLeague,
} from 'api/league/LeagueCreation/leagueCreation'
import ButtonSpinner from 'components/global/ButtonSpinner'
import { leagueEventStore } from 'state/league/leagueEvent'
import { failureMessage, infoMessage } from 'api/global'
import OrganizationInput from 'components/global/inputs/OrganizationInput/OrganizationInput'
import VenueModal from 'pages/Events/Views/CreateEvent/Screens/GeneralDetails/GeneralDetailModal/VenueModal'
import { OrganizationVenues } from 'types/organization'

interface LeagueStep1Props {
  handleNext: () => void
  handleBack: () => void
}

const Step1: React.FC<LeagueStep1Props> = ({ handleNext, handleBack }) => {
  const history = useHistory()
  const {
    mutate: createLeagueMutation,
    isLoading: createLeagueMutationLoading,
  } = useCreateLeague()
  const { mutate: updateLeagueMutation, isLoading: updateLoading } =
    useUpdateLeague()
  const { leagueData, setLeagueData, setRequiredDetails } = leagueEventStore(
    store => ({
      leagueData: store.leagueData,
      setLeagueData: store.setLeagueData,
      setRequiredDetails: store.setRequiredDetails,
    }),
  )
  const [selectedOrganization, setSelectedOrganization] =
    useState<OrganizationResp>(leagueData ? leagueData.organization : undefined)
  const [mapData, setMapModalData] = useState<Address | null>(null)
  const [documentIds, setDocumentIds] = useState<string[]>(
    leagueData ? leagueData.coverPhotosIds : [],
  )
  const [photos, setPhotos] = useState<Photo[]>(
    leagueData ? leagueData.coverPhotos : [],
  )
  const [sports, setSports] = useState<CategoryType>(
    leagueData ? leagueData.sport : undefined,
  )
  const [selectedYear, setSelectedYear] = useState<moment.Moment>(
    leagueData ? moment(leagueData.year, 'YYYY') : moment(),
  )
  const [form] = Form.useForm()
  const [selectedSeason, setSelectedSeason] = useState<string>(
    (leagueData ? leagueData.season : 'summer') as string,
  )
  const [selectedLocationType, setSelectedLocationType] = useState<string>(
    leagueData?.venueType || 'physical',
  )
  const [venueModalVisible, setVenueModalVisible] = useState<boolean>(false)
  const [venueDataItem, setVenueDataItem] = useState<OrganizationVenues | null>(
    null,
  )

  const formSchema = {
    season: leagueData ? leagueData.season : 'summer',
    year: leagueData ? moment(leagueData.year, 'YYYY') : undefined,
    startDate: leagueData ? moment(leagueData.startDate) : undefined,
    endDate: leagueData ? moment(leagueData.endDate) : undefined,
    sportId: leagueData ? leagueData.sportId : undefined,
    organizationId: leagueData ? leagueData.organizationId : undefined,
    venueType: selectedLocationType || 'physical',
    venueId: leagueData ? leagueData.venue?._id : undefined,
    description: leagueData ? leagueData.description : '',
  }

  const onFinish = (values: LeagueStep1Payload) => {
    if (!selectedOrganization) {
      return failureMessage('Organization is required')
    }
    if (selectedLocationType === 'physical' && !mapData) {
      return failureMessage('Location is required')
    }

    if (selectedLocationType === 'physical' && !venueDataItem) {
      return failureMessage('Venue is required')
    }

    const payload: any = { ...values }
    payload.year = moment(values.year).format('YYYY')
    payload.organizationId = selectedOrganization?._id
    if (selectedLocationType === 'physical' && mapData) {
      payload.locationId = mapData._id
      payload.location = mapData
    } else {
      // Virtual / to-be-determined venues have no address — omit location entirely
      // instead of sending null (backend rejects a null location object).
      delete payload.location
    }
    payload.coverPhotosIds = documentIds
    payload.venueId = venueDataItem?._id

    if (leagueData) {
      if (!leagueData._id)
        return infoMessage("League doesn't exist! something went wrong")
      updateLeagueMutation(
        { leagueId: leagueData._id, payload },
        {
          onSuccess: data => {
            setLeagueData({ ...leagueData, ...data })
            handleNext()
          },
        },
      )
    } else {
      createLeagueMutation(payload, {
        onSuccess: data => {
          setLeagueData(data)
          setRequiredDetails(data.requiredDetails)
          handleNext()
        },
      })
    }
  }

  useEffect(() => {
    if (leagueData) {
      form.setFieldsValue({
        season: leagueData.season,
        year: moment(leagueData.year, 'YYYY'),
        startDate: moment(leagueData.startDate),
        endDate: moment(leagueData.endDate),
        sportId: leagueData.sportId,
        organizationId: leagueData.organizationId,
        // the radio field is `venueType` — restore the actual saved value
        // (physical / virtual / to-be-determined), falling back to the old
        // location-based inference only for legacy leagues without venueType.
        venueType:
          leagueData.venueType ||
          (leagueData.location ? 'physical' : 'virtual'),
      })
      setMapModalData(leagueData.location)
      setSelectedOrganization(leagueData.organization)
      setSports(leagueData.sport)
      setDocumentIds(leagueData.coverPhotosIds)
      setPhotos(leagueData.coverPhotos)
      setSelectedLocationType(
        leagueData.venueType ||
          (leagueData.location ? 'physical' : 'virtual'),
      )
      setVenueDataItem(leagueData.venue || null)
    }
  }, [leagueData])

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        initialValues={formSchema}
        onFinish={onFinish}
      >
        <OrganizationInput
          selectedOrganization={selectedOrganization}
          setSelectedOrganization={setSelectedOrganization}
        />
        <Row>
          <Col span={24}>
            <label className="input-label required-input">Sport</label>
            <Form.Item
              name="sportId"
              className="form-control allergies-field "
              rules={[{ required: true, message: 'Sport is required' }]}
            >
              <SportsSelect data={sports} form={form} setSports={setSports} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={10}>
          <Col className="">
            <label className="input-label required-input">
              Select a season
            </label>
            <Form.Item
              className="form-control mb-0"
              name="season"
              rules={[{ required: true, message: 'Season is required' }]}
            >
              <Radio.Group
                defaultValue={'summer'}
                onChange={e => setSelectedSeason(e.target.value)}
              >
                <Radio value={'summer'} className="mr-40 radio">
                  Summer
                </Radio>
                <Radio value={'fall'} className="mr-40 radio">
                  Fall
                </Radio>
                <Radio value={'winter'} className="mr-40 radio">
                  Winter
                </Radio>
                <Radio value={'spring'} className="mr-40 radio">
                  Spring
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Row className="mt-10">
          <Col span={24}>
            <label className="input-label required-input">Year</label>
            <Form.Item
              name="year"
              rules={[{ required: true, message: 'Year is required' }]}
            >
              <DatePicker
                name="year"
                picker="year"
                className="selector w-full"
                disabledDate={current =>
                  current && current.year() < new Date().getFullYear()
                }
                onChange={value => {
                  setSelectedYear(value)
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row className="mt-10">
          <Col span={24}>
            <label className="input-label required-input">Location</label>
            <Form.Item
              name="venueType"
              rules={[{ required: true, message: 'Venue type is required' }]}
            >
              <Radio.Group
                defaultValue={selectedLocationType}
                onChange={e => setSelectedLocationType(e.target.value)}
                className="flex flex-col gap-10px"
              >
                <Radio value={'physical'} className="mr-40 radio">
                  In person
                </Radio>

                {selectedLocationType === 'physical' &&
                  selectedOrganization && (
                    <Row className="-mt-10px">
                      <Col span={24}>
                        <Location
                          selectedOrganization={selectedOrganization}
                          mapData={mapData}
                          setMapModalData={setMapModalData}
                          form={form}
                        />
                      </Col>
                    </Row>
                  )}

                {selectedLocationType === 'physical' && mapData && (
                  <Row className="">
                    <Col span={24}>
                      <label className="input-label required-input">
                        Venue
                      </label>
                      <div
                        className="w-full border-1px radius-10 flex align-center px-12px pointer h-50px"
                        onClick={() => setVenueModalVisible(true)}
                      >
                        {venueDataItem ? (
                          <div className="w-full flex justify-between align-center">
                            <p className="text-gray">{`${venueDataItem.name}`}</p>
                          </div>
                        ) : (
                          <p className="text-gray">Select Venue</p>
                        )}
                      </div>
                    </Col>
                  </Row>
                )}

                <Radio value={'virtual'} className="mr-40 radio">
                  Online
                </Radio>
                <Radio value={'to-be-determined'} className="mr-40 radio">
                  To be determined
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={10}>
          <Col>
            <label className="input-label">Description</label>
            <Form.Item className="form-control" name="description">
              <Input.TextArea
                className="answer-field"
                placeholder="Few words about the event"
                autoSize={{ minRows: 5, maxRows: 8 }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={18}>
            <label className="input-label">Add Cover</label>
            <UploadDocument
              documentIds={documentIds}
              setDocumentIds={setDocumentIds}
              multiple={true}
              docType="image/*"
              files={photos}
              uploadMultipleAtOnce={true}
            />
          </Col>
        </Row>

        <Row justify="space-between" className="mb-26 mt-40px">
          <Button className="cancel-button" onClick={() => history.goBack()}>
            Back to listing
          </Button>

          <Button
            className="stepper-button"
            disabled={createLeagueMutationLoading || updateLoading}
            htmlType="submit"
          >
            {createLeagueMutationLoading || updateLoading ? (
              <ButtonSpinner size={20} color="#000" />
            ) : (
              'Next'
            )}
          </Button>
        </Row>
      </Form>

      {venueModalVisible && (
        <VenueModal
          mapData={mapData}
          setVenueModalVisible={setVenueModalVisible}
          setFieldsValue={(venue: OrganizationVenues) => {
            form.setFieldsValue({ venue: venue.name })
          }}
          organizationId={selectedOrganization?._id}
          setVenueDataItem={setVenueDataItem}
        />
      )}
    </div>
  )
}

export default Step1
